import { nodePathToFriendlyName, cancelCodeInformationalArray } from '@ourbranch/lookups';
import { policyDetailsFromSegments, getMiAutoPipChanges } from '@ourbranch/policy-utils';
import up from 'core/assets/svg/up.svg';
import down from 'core/assets/svg/down.svg';
import { toCapitalized } from 'core/helpers/formatters';

export function getPriceDetails({ newTotalPrice, originalTotalPrice }) {
  if (newTotalPrice > originalTotalPrice) {
    return {
      arrow: up,
      label: 'increase'
    };
  }
  if (newTotalPrice < originalTotalPrice) {
    return {
      arrow: down,
      label: 'decrease'
    };
  }
  return {
    arrow: null,
    label: 'not change'
  };
}

/**
 *
 * @param {string} changes - the node paths of the changes in a comma separated string
 * @param {array} additionalItems - user friendly strings to also be displayed as items changed to the user
 * @returns
 */
export function parseCoverageChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

export function parsePreviewChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

export function determineBoxTwoText(cancelReason) {
  return cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    .boxTwoText;
}

export function canAddToInternalNote(cancelReason) {
  return !!cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    .allowFreeFormNote;
}

export const getButtonLabel = (action, { needsUnderwritingReview } = {}) => {
  if (action === 'rescind') {
    return 'Rescind cancellation';
  }
  if (action === 'reinstate') {
    return 'Reinstate policy';
  }
  if (action === 'cancel') {
    return 'Cancel policy';
  }

  if (action === 'draft') {
    return 'Keep drafting changes';
  }

  if (action === 'complete') {
    if (needsUnderwritingReview) {
      return 'Continue to underwriting review';
    }

    return 'Continue';
  }

  return 'Save policy changes';
};

const isValuePresentInObject = (keys, obj, value) => {
  for (const key of keys) {
    if (obj.hasOwnProperty(key) && obj[key] === value) {
      return true;
    }
  }
  return false;
};

export const getNotes = ({ action, cancelReason, nonRenewReason, itemsChanged, diff }) => {
  const cancelBoxTwoText =
    action === 'cancel' &&
    cancelReason !== 'NA' &&
    (itemsChanged?.includes('cancelReason') || itemsChanged?.includes('endDate'))
      ? determineBoxTwoText(cancelReason)
      : undefined;

  if (cancelBoxTwoText) {
    //  some pre-determined notes based on cancel reasons cannot be edited
    const disableInternalNote = cancelBoxTwoText && !canAddToInternalNote(cancelReason);
    return { notesText: cancelBoxTwoText, cancelBoxTwoText, disableInternalNote };
  }

  const nonRenewBoxTwoText =
    nonRenewReason && nonRenewReason !== 'NA' && itemsChanged?.includes('autoRenew')
      ? determineBoxTwoText(nonRenewReason)
      : undefined;

  if (nonRenewBoxTwoText) {
    return { notesText: nonRenewBoxTwoText };
  }

  // Garage address rule exception approved
  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges;
  if (addedChanges) {
    const garageAddressRuleExceptionKeys = Object.keys(addedChanges).filter((k) =>
      k.includes('isGarageAddressRuleExceptionApproved')
    );
    const approvingGarageAddressRuleException = isValuePresentInObject(
      garageAddressRuleExceptionKeys,
      addedChanges,
      true
    );
    if (approvingGarageAddressRuleException) {
      return { notesText: 'Underwriting approved garage address rule exception' };
    }
  }

  return { notesText: '' };
};

export const getChangesForApproval = ({ policy, preview, session }) => {
  if (!preview || !policy || policy?.state !== 'MI' || policy.policyType !== 'A') {
    return {};
  }
  const {
    diff,
    createdBy: { isAgency: requestedByIA }
  } = preview;
  const policyDetails = policyDetailsFromSegments(policy);
  const { canApproveUWReview } = session;

  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges || {};
  const removedChanges = diffObj?.full?.removedChanges || {};

  // dont run for cancels
  if (addedChanges?.cancelled === true) {
    return {};
  }

  const {
    needsProofOfEligibilityDocuments: showProofOfEligibilityDocumentsBlock,
    householdResidentsChanged: showAllHouseholdResidentsIncludedBlock,
    needsNewPIPForm: showNewPIPFormRequiredBlock,
    hasChangesThatNeedApproval
  } = getMiAutoPipChanges({
    addedChanges,
    removedChanges,
    policyDetails,
    canApproveUWReview,
    isAgency: requestedByIA
  });

  return {
    showProofOfEligibilityDocumentsBlock,
    showAllHouseholdResidentsIncludedBlock,
    showNewPIPFormRequiredBlock,
    hasChangesThatNeedApproval
  };
};
